<template>
  <DataLayout
    class="data-item-text-area"
    :title="title"
    :instructions="placeholder"
    :instruction-id="instructionId"
    :sub-title="subTitle"
    :is-dl="isDl"
  >
    <v-textarea
      ref="textArea"
      :value="model"
      :error="hasError()"
      :error-messages="extractError() || maxLengthError()"
      outlined
      single-line
      auto-grow
      :aria-label="ariaLabel"
      :aria-describedby="ariaDescribedBy"
      :required="isRequired"
      :aria-required="isRequired"
      @input="
        [sanitizeAndUpdateInput($event), updateMaxLengthSRMessage($event)]
      "
      @paste="pasteInput"
    >
      <template slot="message" slot-scope="{ message }">
        <div aria-hidden="true">{{ message }}</div>
      </template>
    </v-textarea>

    <div
      :class="
        maxLengthMessage || characterWarningMessage
          ? 'data-item-text-area__message-container'
          : ''
      "
    >
      <div
        v-if="!maxWordsLimitError"
        aria-hidden="true"
        class="font-small ml-3 mt-n6 mb-3"
      >
        {{ maxLengthMessage }}
      </div>
      <span :id="staticSRHint" class="d-sr-only">
        {{ limitSRHintMessage }}
      </span>
      <span role="status" aria-atomic="true" class="d-sr-only">
        {{ maxLengthSRMessage }}</span
      >

      <CharacterInputWarningMessage
        v-if="characterWarningMessage"
        :instruction-id="characterInputWarningId"
        :field-name="fieldName"
      ></CharacterInputWarningMessage>
    </div>

    <slot></slot>
  </DataLayout>
</template>

<script>
import hasError from "~/components/forms/utilities/hasError";
import extractError from "~/components/forms/utilities/extractError";
import debounce from "~/store/manage/utilities/debounce";

import DataLayout from "~/components/forms/components/layout/DataLayout";
import CharacterInputWarningMessage from "~/components/forms/components/shared/CharacterInputWarningMessage";

import {
  characterCount,
  wordCount,
  maxLengthMessage,
  maxLengthError,
  limitSRHintMessage,
} from "~/components/forms/utilities/modelMaxLength";
import {
  sanitizeAndUpdateInput,
  pasteInput,
} from "~/components/forms/utilities/sanitizeCharacterInput";

export default {
  name: "DataItemTextArea",
  components: {
    DataLayout,
    CharacterInputWarningMessage,
  },
  props: {
    item: { type: Object, default: () => {} },
    fieldName: { type: String, required: true },
    updateAction: { type: String, default: () => undefined },
    isRequired: { type: Boolean, default: false },

    errors: { type: Array, default: () => [] },

    title: { type: String, default: "" },
    subTitle: { type: String, default: "" },
    isDl: { type: Boolean, default: false },
    label: { type: String, default: "" },

    index: { type: Number, default: undefined },

    placeholder: { type: String, default: undefined },
    maxCharacters: { type: Number, default: undefined },
    maxWords: { type: Number, default: undefined },

    sanitizeCharacterInput: { type: Boolean, default: false },
  },
  data() {
    return {
      componentId: null,
      model: "",
      pastedText: null,
      characterWarningMessage: false,
      maxLengthSRMessage: undefined,
    };
  },
  computed: {
    instructionId() {
      return this.title.replace(/[* ]/g, "") + this.componentId;
    },
    maxLengthMsgId() {
      return `MaxLengthMsg${this.componentId}`;
    },
    maxLengthExceededMsgId() {
      return `MaxLengthExceededMsg${this.componentId}`;
    },
    characterInputWarningId() {
      return `CharacterInputWarningMsg${this.componentId}`;
    },
    staticSRHint() {
      return `MessageStaticHint${this.componentId}`;
    },
    ariaLabel() {
      return this.title ? this.title.toLowerCase() : undefined;
    },
    ariaDescribedBy() {
      if (this.placeholder && this.maxLengthMessage) {
        return `${this.instructionId} ${this.maxLengthExceededMsgId} ${this.maxLengthMsgId} ${this.characterInputWarningId} ${this.staticSRHint}`;
      }

      if (this.placeholder) {
        return `${this.instructionId} ${this.characterInputWarningId}`;
      }

      if (this.maxLengthMessage) {
        return this.maxLengthMsgId;
      }
      return false;
    },
    maxWordsLimitError() {
      return this.wordCount > this.maxWords;
    },
    characterCount,
    wordCount,
    maxLengthMessage,
    limitSRHintMessage,
  },
  mounted() {
    this.componentId = this._uid;
    this.model = this.item?.[this.fieldName] || null;
  },
  created() {
    this.updateMaxLengthSRMessage = debounce(() => {
      this.maxLengthSRMessage = this.maxWordsLimitError
        ? this.maxLengthError()
        : this.maxLengthMessage;
    }, 500);
  },
  methods: {
    hasError,
    extractError,
    maxLengthError,
    sanitizeAndUpdateInput,
    pasteInput,
  },
};
</script>

<style lang="scss">
.data-item-text-area {
  &__message-container {
    margin: 15px 0 10px 0;
  }

  &__max-length-msg {
    margin-left: 10px;
    font-size: 12px;
  }
}
</style>
